import {cellDropAnchor} from '../drag/placement'
import type {Anchor, BoardCardAddress, BoardItemsMove, CellAddress, ColumnKey, ItemId, RowKey} from '../types'

/**
 * The moves a card menu offers, worked out from the board's data and the caller's rules.
 *
 * No React and no DOM, so every rule here is exercised without a rendered board, the way
 * `drag/placement.ts` and `drag/resolve-drop.ts` keep their own answers pure.
 *
 * **Up and down follow the board's reading order**, which is the order the arrow keys move
 * focus in. Inside a cell the caller ranks by hand they reorder the card among its
 * neighbors. Past either end of such a cell, and anywhere in a cell ordered by a sort, they
 * move the card to the same column in the swimlane above or below. That is what lets a menu
 * make every move a drag can make, one step at a time.
 *
 * Left and right move the card to the column beside it in the same swimlane.
 */

/** `card` runs along the board's reading order; `column` crosses it. */
export type BoardCardMoveAxis = 'card' | 'column'

/** Toward the start of the axis, or toward its end. */
export type BoardCardMoveStep = 'previous' | 'next'

/** Everything about the board a move is decided against. */
export type CardMoveBoard = {
  columnKeys: readonly ColumnKey[]
  rowKeys: readonly RowKey[]
  hasMoreColumns: boolean
  hasMoreRows: boolean
  itemIdsAt: (address: CellAddress) => readonly ItemId[]
  /** Whether a cell has cards that are not loaded, as `cellHasUnloadedItems` answers it. */
  hasUnloadedItemsAt: (address: CellAddress) => boolean
  /** `BoardProvider`'s `canReorderCards`. */
  isHandOrdered: (address: CellAddress) => boolean
  /** The provider's drop policy, which allows the card's own cell without asking. */
  canDrop: (card: BoardCardAddress, to: CellAddress) => boolean
}

export type CardMoveOption = {
  axis: BoardCardMoveAxis
  step: BoardCardMoveStep
  /** Where the card would land, absent when the board holds nothing that way. */
  to?: CellAddress
  /** The move to report, absent when this one cannot be made right now. */
  move?: BoardItemsMove
}

/** The parts of a board that decide which axes a cell offers moves along at all. */
export type CardMoveAxes = Pick<
  CardMoveBoard,
  'columnKeys' | 'rowKeys' | 'hasMoreColumns' | 'hasMoreRows' | 'isHandOrdered'
>

/**
 * The axes a card in this cell can ever move along, whatever its place in the cell and
 * whether the caller lets it move: up and down on a board with more than one swimlane or in
 * a cell the caller ranks by hand, and left and right on a board with more than one column.
 * Swimlanes and columns still to load count. Empty on a fully loaded one-cell board whose
 * cell is sorted.
 */
export function cardMoveAxes(cell: CellAddress, board: CardMoveAxes): BoardCardMoveAxis[] {
  const axes: BoardCardMoveAxis[] = []
  if (board.rowKeys.length > 1 || board.hasMoreRows || board.isHandOrdered(cell)) axes.push('card')
  if (board.columnKeys.length > 1 || board.hasMoreColumns) axes.push('column')

  return axes
}

/**
 * The moves to offer one card, up and down first and then left and right.
 *
 * A direction along an axis `cardMoveAxes` leaves out is left out here too, rather than
 * offered and refused. A direction that runs out at an edge, waits on cards or columns
 * nobody has loaded, or is refused by the caller is offered with no `move`.
 *
 * `canMove` is the card's own answer: the board moves cards, the card has an id, and the
 * caller's `getItemDragState` does not refuse it. When it is `false` every direction is
 * offered with no `move`, so a menu can still say why.
 */
export function cardMoveOptions(card: BoardCardAddress, board: CardMoveBoard, canMove: boolean): CardMoveOption[] {
  const columnIndex = board.columnKeys.indexOf(card.columnKey)
  const rowIndex = board.rowKeys.indexOf(card.rowKey)
  const itemIds = board.itemIdsAt(card)
  const index = itemIds.indexOf(card.itemId)
  // A card the data no longer holds where it is drawn has nothing to move from.
  const isPlaced = columnIndex !== -1 && rowIndex !== -1 && index !== -1

  const axes = cardMoveAxes(card, board)
  const options: CardMoveOption[] = []

  if (axes.includes('card')) {
    const place = {rowIndex, itemIds, index}
    options.push(stepAlongCards(card, board, 'previous', place), stepAlongCards(card, board, 'next', place))
  }

  if (axes.includes('column')) {
    options.push(
      stepAcrossColumns(card, board, 'previous', columnIndex),
      stepAcrossColumns(card, board, 'next', columnIndex),
    )
  }

  if (canMove && isPlaced) return options

  return options.map(({axis, step, to}) => (to === undefined ? {axis, step} : {axis, step, to}))
}

type Place = {rowIndex: number; itemIds: readonly ItemId[]; index: number}

function stepAlongCards(
  card: BoardCardAddress,
  board: CardMoveBoard,
  step: BoardCardMoveStep,
  {rowIndex, itemIds, index}: Place,
): CardMoveOption {
  const axis = 'card'

  if (board.isHandOrdered(card)) {
    // The insertion indexes a drop would use, so a menu reorder and a drop agree.
    const hasNeighbor = step === 'previous' ? index > 0 : index !== -1 && index < itemIds.length - 1
    if (hasNeighbor) {
      const insertionIndex = step === 'previous' ? index - 1 : index + 2
      const anchor = cellDropAnchor(itemIds, insertionIndex, {hasMoreItems: board.hasUnloadedItemsAt(card)})

      return option(axis, step, card, cellOf(card), anchor, board)
    }

    // The last loaded card of a cell with more to come has unloaded cards below it, and
    // stepping into the next swimlane would skip over them.
    if (step === 'next' && board.hasUnloadedItemsAt(card)) return {axis, step, to: cellOf(card)}
  }

  const targetRow = board.rowKeys[step === 'previous' ? rowIndex - 1 : rowIndex + 1]
  if (rowIndex === -1 || targetRow === undefined) return {axis, step}

  const to = {columnKey: card.columnKey, rowKey: targetRow}
  // Up lands at the bottom of the cell above and down at the top of the cell below, which
  // is where the card is read next.
  return option(axis, step, card, to, landing(board, to, step === 'previous' ? 'end' : 'start'), board)
}

function stepAcrossColumns(
  card: BoardCardAddress,
  board: CardMoveBoard,
  step: BoardCardMoveStep,
  columnIndex: number,
): CardMoveOption {
  const axis = 'column'
  const targetColumn = board.columnKeys[step === 'previous' ? columnIndex - 1 : columnIndex + 1]
  if (columnIndex === -1 || targetColumn === undefined) return {axis, step}

  const to = {columnKey: targetColumn, rowKey: card.rowKey}

  return option(axis, step, card, to, landing(board, to, 'start'), board)
}

/**
 * Where a card lands in a cell it moves into: an anchor on a hand-ordered cell, and none
 * on a sorted one, whose sort places it. The same rule a drop follows.
 */
function landing(board: CardMoveBoard, to: CellAddress, end: 'start' | 'end'): Anchor | undefined {
  if (!board.isHandOrdered(to)) return undefined

  const itemIds = board.itemIdsAt(to)

  return cellDropAnchor(itemIds, end === 'start' ? 0 : itemIds.length, {hasMoreItems: board.hasUnloadedItemsAt(to)})
}

function option(
  axis: BoardCardMoveAxis,
  step: BoardCardMoveStep,
  card: BoardCardAddress,
  to: CellAddress,
  anchor: Anchor | undefined,
  board: CardMoveBoard,
): CardMoveOption {
  if (!board.canDrop(card, to)) return {axis, step, to}

  const move: BoardItemsMove = {itemIds: [card.itemId], from: cellOf(card), to}

  return {axis, step, to, move: anchor === undefined ? move : {...move, anchor}}
}

function cellOf({columnKey, rowKey}: CellAddress): CellAddress {
  return {columnKey, rowKey}
}
